import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Fallback admin emails for development environment
const FALLBACK_ADMIN_EMAILS = [
  'admin@strategiz.io',
  'test@gmail.com',
  'test1@gmail.com',
  'test2@gmail.com' // Make sure this is included
];

// Get the current environment
const isProduction = process.env.NODE_ENV === 'production';
const envPrefix = isProduction ? 'prod' : 'dev';

/**
 * Custom hook to fetch the admin emails list from Firestore.
 * Falls back to an empty array if not found.
 */
export function useAdminEmails() {
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAdminEmails() {
      setLoading(true);
      setError(null);
      try {
        // Try to get environment-specific admin emails first
        const envDocRef = doc(db, 'admin_config', `${envPrefix}_admin_emails`);
        const envDocSnap = await getDoc(envDocRef);
        
        if (envDocSnap.exists()) {
          // Use environment-specific admin emails if they exist
          const data = envDocSnap.data();
          setAdminEmails(Array.isArray(data.emails) ? data.emails : []);
          console.log(`Using ${envPrefix} admin emails:`, data.emails);
        } else {
          // Fall back to global admin emails
          const globalDocRef = doc(db, 'admin_config', 'admin_emails');
          const globalDocSnap = await getDoc(globalDocRef);
          
          if (globalDocSnap.exists()) {
            const data = globalDocSnap.data();
            setAdminEmails(Array.isArray(data.emails) ? data.emails : []);
            console.log('Using global admin emails:', data.emails);
          } else {
            // If no Firestore documents exist, use fallback hardcoded list
            console.log('No admin emails found in Firestore, using fallback list');
            setAdminEmails(FALLBACK_ADMIN_EMAILS);
          }
        }
      } catch (err: any) {
        console.error('Error fetching admin emails:', err);
        setError(err.message || 'Failed to fetch admin emails');
        // Use fallback list on error
        setAdminEmails(FALLBACK_ADMIN_EMAILS);
      } finally {
        setLoading(false);
      }
    }
    fetchAdminEmails();
  }, []);

  // Optionally expose a setter for admin UI
  const updateAdminEmails = async (emails: string[]) => {
    try {
      await setDoc(doc(db, 'admin_config', 'admin_emails'), { emails });
      setAdminEmails(emails);
    } catch (err: any) {
      setError(err.message || 'Failed to update admin emails');
    }
  };

  return { adminEmails, loading, error, updateAdminEmails };
}
